import React, { useState, useEffect, useMemo, useCallback } from 'react'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Collapse from '@mui/material/Collapse'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import CircularProgress from '@mui/material/CircularProgress'
import LoadingButton from '@mui/lab/LoadingButton'
import { DataGrid } from '@mui/x-data-grid'
import { alpha } from '@mui/material/styles'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import SearchIcon from '@mui/icons-material/Search'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import CloudDownloadIcon from '@mui/icons-material/CloudDownload'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import ComputerIcon from '@mui/icons-material/Computer'
import DnsIcon from '@mui/icons-material/Dns'

import { FC } from '../../Services'
import { useAuth } from '../../Provider/AuthProvider'
import { useCQuery, invalidateQuery } from '../../Services/QueryCache'
import { ConfirmNameDeleteDialog } from '../../Components/ConfirmNameDeleteDialog'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'

const STATUS_MAP = {
  running: { label: 'Online', color: '#4caf50', bg: '#e8f5e9' },
  created: { label: 'Creato', color: '#2196f3', bg: '#e3f2fd' },
  pending: { label: 'In Aggiornamento', color: '#ff9800', bg: '#fff3e0' },
  maintenance: { label: 'In Manutenzione', color: '#9c27b0', bg: '#f3e5f5' },
  offline: { label: 'Offline', color: '#f44336', bg: '#ffebee' }
}

const STAT_CARDS = [
  { key: 'running', label: 'Online', color: '#4caf50' },
  { key: 'created', label: 'Creato', color: '#2196f3' },
  { key: 'pending', label: 'In Aggiornamento', color: '#ff9800' },
  { key: 'maintenance', label: 'In Manutenzione', color: '#9c27b0' },
  { key: 'offline', label: 'Offline', color: '#f44336' },
  { key: 'all', label: 'Totale', color: '#546e7a' }
]

const formatDate = (isoString) => {
  if (!isoString) return ''
  return new Date(isoString).toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  })
}

const formatSize = (bytes) => {
  if (!bytes) return ''
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const extractClients = (raw) => {
  if (!raw) return []
  if (Array.isArray(raw)) return raw
  if (Array.isArray(raw.clients)) return raw.clients
  if (Array.isArray(raw.data)) return raw.data
  return []
}

const Dashboard = () => {
  const { user } = useAuth() || {}
  const currentUser = user?.user
  const isSiapAdmin = ['siapAdmin', 'siapDeveloper'].includes(currentUser?.role)

  const { data: clientsRaw = [], isLoading } = useCQuery('clients')
  const clients = useMemo(() => extractClients(clientsRaw), [clientsRaw])

  const [isGenerating, setIsGenerating] = useState(false)
  const [clientZipStatus, setClientZipStatus] = useState(null)
  const [copied, setCopied] = useState(false)
  const [zipExpanded, setZipExpanded] = useState(true)
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')

  const [deleteClient, setDeleteClient] = useState(null)
  const [isDeletingClient, setIsDeletingClient] = useState(false)

  const stats = useMemo(() => ({
    total: clients.length,
    running: clients.filter(c => c.status === 'running').length,
    created: clients.filter(c => c.status === 'created').length,
    pending: clients.filter(c => c.status === 'pending').length,
    maintenance: clients.filter(c => c.status === 'maintenance').length,
    offline: clients.filter(c => c.status === 'offline').length
  }), [clients])

  const rows = useMemo(() => {
    let filtered = filter === 'all' ? clients : clients.filter(c => c.status === filter)
    if (search) {
      const q = search.toLowerCase()
      filtered = filtered.filter(c =>
        (c._id || '').toLowerCase().includes(q) ||
        (c.clientId || '').toLowerCase().includes(q) ||
        (c.agencyId || '').toLowerCase().includes(q) ||
        (c.agencyName || '').toLowerCase().includes(q) ||
        (c.additionalInfo?.ip || '').includes(q)
      )
    }
    return filtered
  }, [clients, filter, search])

  // --- Admin-only: zip status ---
  const fetchClientZipStatus = useCallback(async () => {
    try {
      setClientZipStatus(await FC.service('versions').get('client-status'))
    } catch (err) {
      console.error('Errore nel caricamento stato client zip:', err)
    }
  }, [])

  useEffect(() => {
    if (isSiapAdmin) fetchClientZipStatus()
  }, [isSiapAdmin, fetchClientZipStatus])

  const handleGenerate = async () => {
    setIsGenerating(true)
    try {
      const result = await FC.service('versions').create({ action: 'generate-client' })
      window.growl?.show({
        severity: 'success', summary: 'Client generato',
        detail: `CRSLink2.zip generato con successo per ambiente ${result.environment}.`
      })
      setClientZipStatus({
        exists: true, url: result.url, generatedAt: result.generatedAt,
        environment: result.environment, size: result.size
      })
    } catch (err) {
      window.growl?.show({ severity: 'error', summary: 'Errore generazione', detail: err.message })
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopyUrl = async () => {
    if (!clientZipStatus?.url) return
    try {
      await navigator.clipboard.writeText(clientZipStatus.url)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Errore nella copia:', err)
    }
  }

  const handleDeleteClient = async () => {
    if (!deleteClient?._id) return
    setIsDeletingClient(true)
    try {
      await FC.service('clients').remove(deleteClient._id)
      window.growl?.show({ severity: 'success', summary: 'Client eliminato', detail: 'Eliminazione completata.' })
      setDeleteClient(null)
      invalidateQuery('clients')
      invalidateQuery('clientsByAgency')
    } catch (err) {
      window.growl?.show({ severity: 'error', summary: 'Eliminazione non riuscita', detail: err?.message || 'Errore' })
    } finally {
      setIsDeletingClient(false)
    }
  }

  const columns = useMemo(() => [
    {
      field: 'status',
      headerName: 'Stato',
      width: 80,
      sortable: false,
      renderCell: ({ value }) => {
        const cfg = STATUS_MAP[value] || STATUS_MAP.offline
        return (
          <Tooltip title={cfg.label} placement="top">
            <Box sx={{
              width: 14, height: 14, borderRadius: '50%',
              bgcolor: cfg.color, flexShrink: 0
            }} />
          </Tooltip>
        )
      }
    },
    { field: '_id', headerName: 'Client ID', flex: 1, minWidth: 180 },
    ...(isSiapAdmin ? [
      { field: 'agencyId', headerName: 'ID Agenzia', flex: 0.8, minWidth: 130 },
      { field: 'agencyName', headerName: 'Nome Agenzia', flex: 1, minWidth: 150 }
    ] : []),
    {
      field: 'version',
      headerName: 'Versione',
      width: 110,
      renderCell: ({ value }) => value
        ? <Chip label={`v${value}`} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 12 }} />
        : <Typography variant="body2" color="text.disabled">—</Typography>
    },
    {
      field: 'machineName',
      headerName: 'Nome macchina',
      width: 140,
      valueGetter: (value, row) => row?.additionalInfo?.machineName || '—',
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontSize: 13 }}>
          {value || '—'}
        </Typography>
      )
    },
    {
      field: 'ip',
      headerName: 'IP',
      width: 140,
      valueGetter: (value, row) => row?.additionalInfo?.ip || '',
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 13 }}>
          {value || '—'}
        </Typography>
      )
    },
    {
      field: 'platform',
      headerName: 'Piattaforma',
      width: 140,
      valueGetter: (value, row) => row?.additionalInfo?.platform || '—'
    },
    {
      field: 'createdAt',
      headerName: 'Creato il',
      width: 170,
      renderCell: ({ value }) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
          {value ? formatDate(value) : '—'}
        </Typography>
      )
    },
    ...(isSiapAdmin
      ? [{
        field: 'actions',
        headerName: 'Azioni',
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const canDelete = row.status === 'offline' || row.status === 'created'
          return (
            <Tooltip
              title={canDelete ? 'Elimina client' : 'Eliminazione consentita solo se il client è Creato o Offline'}
              placement="left"
            >
              <span>
                <IconButton
                  size="small"
                  color="error"
                  disabled={!canDelete}
                  onClick={() => canDelete && setDeleteClient(row)}
                  aria-label={canDelete ? 'Elimina client' : 'Eliminazione non disponibile: stato non consentito'}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )
        }
      }]
      : [])
  ], [isSiapAdmin])

  const NoRowsOverlay = useCallback(() => (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', gap: 1, py: 4 }}>
      <ComputerIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
      <Typography color="text.secondary">
        {filter === 'all' ? 'Nessun client registrato' : `Nessun client ${STATUS_MAP[filter]?.label || filter}`}
      </Typography>
    </Stack>
  ), [filter])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Stack alignItems="center" spacing={2}>
          <CircularProgress size={40} />
          <Typography variant="body2" color="text.secondary">Caricamento dashboard...</Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1600, pb: 5 }}>

      <ConfirmNameDeleteDialog
        open={!!deleteClient}
        onClose={() => !isDeletingClient && setDeleteClient(null)}
        title="Elimina client"
        nameToMatch={deleteClient?._id || ''}
        nameFieldLabel="Digita l'ID del client per confermare"
        hint="Per sicurezza, inserisci l'ID esatto del client mostrato nella colonna «Client ID»."
        onConfirm={handleDeleteClient}
        isProcessing={isDeletingClient}
      />

      {/* Page Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5, mt: 1 }}>
        <DnsIcon color="primary" sx={{ fontSize: 28 }} />
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Dashboard
        </Typography>
        <Chip
          label={`${stats.total} client`}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      </Stack>

      {/* Admin-only: Client Package */}
      {isSiapAdmin && (
        <Paper
          elevation={0}
          sx={{ mb: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
        >
          <Box
            onClick={() => setZipExpanded(prev => !prev)}
            role="button"
            tabIndex={0}
            aria-expanded={zipExpanded}
            aria-label="Toggle Client Package section"
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setZipExpanded(prev => !prev) }}
            sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              px: 2.5, py: 1.5, cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CloudDownloadIcon color="primary" fontSize="small" />
              <Typography variant="subtitle1" fontWeight={600}>Client Package</Typography>
            </Stack>
            {zipExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>

          <Collapse in={zipExpanded}>
            <Divider />
            <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" useFlexGap>
                <LoadingButton
                  variant="contained"
                  loading={isGenerating}
                  onClick={handleGenerate}
                  startIcon={<CloudDownloadIcon />}
                  size="small"
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  Genera Client
                </LoadingButton>
              </Stack>

              {clientZipStatus?.exists && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary">
                      URL Download
                    </Typography>
                    <Tooltip title={copied ? 'Copiato!' : 'Copia URL'}>
                      <IconButton size="small" onClick={handleCopyUrl} aria-label="Copia URL download">
                        {copied
                          ? <CheckIcon fontSize="small" color="success" />
                          : <ContentCopyIcon sx={{ fontSize: 14 }} />
                        }
                      </IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography sx={{
                    fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all',
                    bgcolor: 'grey.100', px: 1.5, py: 1, borderRadius: 1, mb: 1.5, userSelect: 'all'
                  }}>
                    {clientZipStatus.url}
                  </Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                    {clientZipStatus.generatedAt && (
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(clientZipStatus.generatedAt)}
                      </Typography>
                    )}
                    {clientZipStatus.environment && (
                      <Chip label={clientZipStatus.environment} size="small" color="primary" variant="outlined" />
                    )}
                    {clientZipStatus.size && (
                      <Typography variant="caption" color="text.secondary">
                        {formatSize(clientZipStatus.size)}
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Stats Cards */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' },
        gap: 1.5, mb: 2.5
      }}>
        {STAT_CARDS.map(({ key, label, color }) => {
          const isActive = filter === key
          const count = key === 'all' ? stats.total : stats[key]
          return (
            <Paper
              key={key}
              elevation={0}
              onClick={() => setFilter(key)}
              role="button"
              tabIndex={0}
              aria-label={`Filtra per ${label}`}
              aria-pressed={isActive}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setFilter(key) }}
              sx={{
                p: 2, cursor: 'pointer',
                border: '1px solid', borderColor: isActive ? color : 'divider',
                borderLeft: `4px solid ${color}`,
                borderRadius: 2,
                transition: 'all 0.15s ease',
                bgcolor: isActive ? alpha(color, 0.06) : 'background.paper',
                '&:hover': { borderColor: color, bgcolor: alpha(color, 0.04) }
              }}
            >
              <Typography variant="caption" color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500, fontSize: 11 }}>
                {label}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color, mt: 0.25, lineHeight: 1.2 }}>
                {count}
              </Typography>
            </Paper>
          )
        })}
      </Box>

      {/* Client Table */}
      <Paper
        elevation={0}
        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
      >
        <Box sx={{
          px: 2.5, py: 1.5, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5
        }}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ComputerIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {filter !== 'all' ? `Client ${STATUS_MAP[filter]?.label}` : 'Tutti i Client'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {rows.length} di {stats.total}
              </Typography>
            </Box>
          </Stack>
          <TextField
            placeholder="Cerca client..."
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Cerca client per ID, agenzia o IP"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                )
              }
            }}
            sx={{ minWidth: 220, maxWidth: 300 }}
          />
        </Box>

        <Divider />

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row._id}
          autoHeight
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: 'status', sort: 'asc' }] }
          }}
          slots={{ noRowsOverlay: NoRowsOverlay }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
            '& .MuiDataGrid-row:hover': { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }
          }}
        />
      </Paper>
    </Box>
  )
}

export default Dashboard
